import React, { useEffect, useMemo, useState } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, Image } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import FeatherIcon from 'react-native-vector-icons/Feather';
import { useAuth } from '../context/AuthContext';
import { NotificationPreferencesSection } from '../components/settings/NotificationPreferencesSection';
import { useStudentProfilePhoto } from '../hooks/useStudentProfilePhoto';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { WebPageShell } from '../components/common/WebPageShell';
import { Logger } from '../utils/logger';
import { useTheme, ThemeColors } from '../context/ThemeContext';
import { BUILD_LABEL } from '../buildInfo';
import {
  getLanguage,
  setLanguage as persistLanguage,
  getRegion,
  setRegion as persistRegion,
  LANGUAGE_OPTIONS,
  REGION_OPTIONS,
} from '../services/localPreferenceService';
import { OptionPickerModal } from '../components/settings/OptionPickerModal';
import { HelpCenterModal } from '../components/settings/HelpCenterModal';

type NavigationProp = NativeStackNavigationProp<any>;

interface SettingsToggleRowProps {
  title: string;
  subtitle: string;
  value: boolean;
  onValueChange: (val: boolean) => void;
  isLast?: boolean;
}

const CustomSwitch = ({ value, onValueChange }: { value: boolean, onValueChange: (val: boolean) => void }) => {
  const { colors } = useTheme();
  const styles = useMemo(() => createStyles(colors), [colors]);
  return (
    <TouchableOpacity
      activeOpacity={0.8}
      onPress={() => onValueChange(!value)}
      style={[
        styles.customSwitch,
        { backgroundColor: value ? colors.primary : colors.border }
      ]}>
      <View
        style={[
          styles.customSwitchThumb,
          value ? styles.customSwitchThumbOn : styles.customSwitchThumbOff
        ]}
      />
    </TouchableOpacity>
  );
};

const SettingsToggleRow = ({ title, subtitle, value, onValueChange, isLast }: SettingsToggleRowProps) => {
  const { colors } = useTheme();
  const styles = useMemo(() => createStyles(colors), [colors]);
  return (
    <View style={styles.settingsRow}>
      <View style={styles.settingsRowText}>
        <Text style={styles.settingsRowTitle}>{title}</Text>
        <Text style={styles.settingsRowSubtitle}>{subtitle}</Text>
      </View>
      <CustomSwitch value={value} onValueChange={onValueChange} />
      {!isLast && <View style={styles.divider} />}
    </View>
  );
};

interface SettingsLinkRowProps {
  title: string;
  subtitle?: string;
  isLast?: boolean;
  onPress?: () => void;
  disabled?: boolean;
}

const SettingsLinkRow = ({ title, subtitle, isLast, onPress, disabled }: SettingsLinkRowProps) => {
  const { colors } = useTheme();
  const styles = useMemo(() => createStyles(colors), [colors]);
  return (
    <TouchableOpacity style={styles.settingsRow} onPress={onPress} disabled={disabled} activeOpacity={disabled ? 1 : 0.6}>
      <View style={styles.settingsRowText}>
        <Text style={[styles.settingsRowTitle, disabled && styles.settingsRowTitleDisabled]}>{title}</Text>
      </View>
      <View style={styles.linkRight}>
        {subtitle && <Text style={styles.linkSubtitle}>{subtitle}</Text>}
        {!disabled && <FeatherIcon name="chevron-right" size={18} color={colors.textMuted} />}
      </View>
      {!isLast && <View style={styles.divider} />}
    </TouchableOpacity>
  );
};

export function SettingsScreen() {
  const navigation = useNavigation<NavigationProp>();
  const { selectedStudent, logout } = useAuth();
  const { displayPhoto } = useStudentProfilePhoto(selectedStudent?.id || '', selectedStudent || undefined);
  const { colors, isDark, toggleDarkMode } = useTheme();
  const styles = useMemo(() => createStyles(colors), [colors]);

  // "Privacy & access" toggles below (Live class viewing, Chat with teacher,
  // Biometric login) are commented out — none of them do anything: no backend
  // save, and nothing else in the app reads this state to gate Live Class,
  // Chat, or login. Flipping them looked like a real setting but wasn't.
  // const [liveClassViewing, setLiveClassViewing] = useState(true);
  // const [chatWithTeacher, setChatWithTeacher] = useState(false);
  // const [biometricLogin, setBiometricLogin] = useState(false);

  const [language, setLanguageState] = useState('English');
  const [region, setRegionState] = useState('United States');
  const [showLanguagePicker, setShowLanguagePicker] = useState(false);
  const [showRegionPicker, setShowRegionPicker] = useState(false);
  const [showHelpCenter, setShowHelpCenter] = useState(false);

  // Language / Region are device-local prefs (AsyncStorage) — no i18n system to apply
  // them to yet, but the choice itself persists. Dark mode is now the real ThemeContext.
  useEffect(() => {
    (async () => {
      const [savedLanguage, savedRegion] = await Promise.all([getLanguage(), getRegion()]);
      setLanguageState(savedLanguage);
      setRegionState(savedRegion);
    })();
  }, []);

  const handleLanguageSelect = (value: string) => {
    setLanguageState(value);
    persistLanguage(value).catch((error) => Logger.warn('Settings', 'Failed to save language preference', error));
  };

  const handleRegionSelect = (value: string) => {
    setRegionState(value);
    persistRegion(value).catch((error) => Logger.warn('Settings', 'Failed to save region preference', error));
  };

  return (
    <WebPageShell maxWidth={800} title="Settings">
      <View style={styles.content}>

        {/* Profile Card */}
        {selectedStudent && (
          <TouchableOpacity style={styles.profileCard} onPress={() => navigation.navigate('Profile' as any)}>
            <View style={styles.photoContainer}>
              {displayPhoto ? (
                <Image source={{ uri: displayPhoto }} style={styles.photo} />
              ) : (
                <Text style={styles.photoPlaceholder}>{selectedStudent.name.charAt(0).toUpperCase()}</Text>
              )}
            </View>
            <View style={styles.profileInfo}>
              <Text style={styles.profileName}>{selectedStudent.name}</Text>
              <Text style={styles.profileLink}>View full profile</Text>
            </View>
            <FeatherIcon name="chevron-right" size={18} color={colors.textMuted} />
          </TouchableOpacity>
        )}

        {/* Notifications Section */}
        <Text style={styles.sectionHeading}>Notifications</Text>
        <View style={styles.sectionCard}>
          {/* The real category x channel matrix - server-enforced on every send. */}
          <NotificationPreferencesSection />
        </View>

        {/* Privacy & access Section — hidden: none of these toggles do
            anything (see state comment above). Restore once they're wired to
            real backend/OS behavior.
        <Text style={styles.sectionHeading}>Privacy & access</Text>
        <View style={styles.sectionCard}>
          <SettingsToggleRow
            title="Live class viewing"
            subtitle="Watch sessions when enabled"
            value={liveClassViewing}
            onValueChange={setLiveClassViewing}
          />
          <SettingsToggleRow
            title="Chat with teacher"
            subtitle="Message the class teacher"
            value={chatWithTeacher}
            onValueChange={setChatWithTeacher}
          />
          <SettingsToggleRow
            title="Biometric login"
            subtitle="Face / fingerprint unlock"
            value={biometricLogin}
            onValueChange={setBiometricLogin}
            isLast
          />
        </View>
        */}

        {/* Preferences Section */}
        <Text style={styles.sectionHeading}>Preferences</Text>
        <View style={styles.sectionCard}>
          <SettingsToggleRow
            title="Dark mode"
            subtitle="Easier on the eyes at night"
            value={isDark}
            onValueChange={toggleDarkMode}
          />
          <SettingsLinkRow title="Language" subtitle={language} onPress={() => setShowLanguagePicker(true)} />
          <SettingsLinkRow title="Region" subtitle={region} onPress={() => setShowRegionPicker(true)} isLast />
        </View>

        {/* Support Section */}
        <Text style={styles.sectionHeading}>Support</Text>
        <View style={styles.sectionCard}>
          <SettingsLinkRow title="Help centre" onPress={() => setShowHelpCenter(true)} />
          {/* No published privacy policy yet — disabled rather than linking to nothing. */}
          <SettingsLinkRow title="Terms & privacy policy" isLast disabled />
        </View>

        {/* Logout Button */}
        <TouchableOpacity style={styles.logoutButton} onPress={logout}>
          <Text style={styles.logoutButtonText}>Log out</Text>
        </TouchableOpacity>

        <Text style={styles.versionText}>Cayman Academy - Parent App</Text>
        <Text style={styles.versionText}>{BUILD_LABEL}</Text>

      </View>

      <OptionPickerModal
        visible={showLanguagePicker}
        title="Language"
        options={LANGUAGE_OPTIONS}
        selected={language}
        onSelect={handleLanguageSelect}
        onClose={() => setShowLanguagePicker(false)}
      />
      <OptionPickerModal
        visible={showRegionPicker}
        title="Region"
        options={REGION_OPTIONS}
        selected={region}
        onSelect={handleRegionSelect}
        onClose={() => setShowRegionPicker(false)}
      />
      <HelpCenterModal visible={showHelpCenter} onClose={() => setShowHelpCenter(false)} />
    </WebPageShell>
  );
}

const createStyles = (colors: ThemeColors) => StyleSheet.create({
  header: { marginBottom: 20 },
  title: { fontSize: 22, fontWeight: '800', color: colors.textPrimary },
  content: {
    paddingHorizontal: 20,
    paddingTop: 10,
  },
  profileCard: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.card,
    borderRadius: 20,
    padding: 16,
    marginBottom: 24,
    shadowColor: colors.shadow,
    shadowOpacity: 0.05,
    shadowRadius: 10,
    shadowOffset: { width: 0, height: 4 },
    elevation: 2,
  },
  photoContainer: {
    width: 48,
    height: 48,
    borderRadius: 12,
    backgroundColor: colors.primaryLight,
    justifyContent: 'center',
    alignItems: 'center',
    overflow: 'hidden',
    marginRight: 12,
    borderWidth: 1,
    borderColor: colors.border,
  },
  photo: {
    width: '100%',
    height: '100%',
  },
  photoPlaceholder: {
    fontSize: 20,
    fontWeight: '700',
    color: colors.primary,
  },
  profileInfo: {
    flex: 1,
  },
  profileName: {
    fontSize: 16,
    fontWeight: '800',
    color: colors.textPrimary,
    marginBottom: 2,
  },
  profileLink: {
    fontSize: 13,
    color: colors.textSecondary,
    fontWeight: '500',
  },
  sectionHeading: {
    fontSize: 14,
    fontWeight: '800',
    color: colors.textSecondary,
    marginBottom: 12,
    marginLeft: 4,
  },
  sectionCard: {
    backgroundColor: colors.card,
    borderRadius: 20,
    paddingHorizontal: 20,
    paddingVertical: 20,
    marginBottom: 24,
    shadowColor: colors.shadow,
    shadowOpacity: 0.03,
    shadowRadius: 8,
    shadowOffset: { width: 0, height: 2 },
    elevation: 2,
  },
  settingsRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 16,
    position: 'relative',
  },
  settingsRowText: {
    flex: 1,
    paddingRight: 16,
  },
  settingsRowTitle: {
    fontSize: 14,
    fontWeight: '700',
    color: colors.textPrimary,
    marginBottom: 4,
  },
  settingsRowTitleDisabled: {
    color: colors.textMuted,
  },
  settingsRowSubtitle: {
    fontSize: 11,
    color: colors.textMuted,
    fontWeight: '500',
  },
  linkRight: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  linkSubtitle: {
    fontSize: 13,
    color: colors.textMuted,
    fontWeight: '500',
  },
  divider: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: 1,
    backgroundColor: colors.divider,
  },
  customSwitch: {
    width: 44,
    height: 24,
    borderRadius: 12,
    justifyContent: 'center',
    padding: 2,
  },
  customSwitchThumb: {
    width: 20,
    height: 20,
    borderRadius: 10,
    backgroundColor: '#FFFFFF',
    shadowColor: colors.shadow,
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 2,
    elevation: 2,
  },
  customSwitchThumbOn: {
    alignSelf: 'flex-end',
  },
  customSwitchThumbOff: {
    alignSelf: 'flex-start',
  },
  logoutButton: {
    marginTop: 8,
    marginBottom: 16,
    paddingVertical: 16,
    borderRadius: 14,
    borderWidth: 1.5,
    borderColor: '#F97316',
    backgroundColor: colors.warningLight,
    alignItems: 'center',
    justifyContent: 'center',
  },
  logoutButtonText: {
    color: '#F97316',
    fontSize: 15,
    fontWeight: '700',
  },
  versionText: {
    textAlign: 'center',
    fontSize: 12,
    color: colors.textMuted,
    fontWeight: '500',
    marginBottom: 40,
  },
});
